import { datadogRum } from "@datadog/browser-rum";
import { stringifyUrl } from "query-string";

import { isBTAdminPath } from "@btnet/services-btnet/btadmin-path";

import { getSnapshotLoginType } from "helpers/globalContext/globalInfoSnapshot";

import { CommonConstants } from "types/constants";
import { BTLoginTypes } from "types/enum";

export interface IIsInPortalProps {
    /** @default false */
    builder?: boolean;

    /** @default false */
    subs?: boolean;

    /** @default false */
    owner?: boolean;

    /** @default false */
    btadmin?: boolean;
}

export interface IPortalType {
    isBuilder: boolean;
    isSubs: boolean;
    isOwner: boolean;
    isBtadmin: boolean;
}

/**
 * @see BTLoginTypes
 * This matches the enum server side BTLoginPortalType - do not change names
 */
export enum PortalType {
    ALL = 0,
    BUILDER = 1,
    OWNER = 2,
    SUBS = 3,
    LEAD = 4,
    BTADMIN = 5,
}

/**
 * @returns true when the current user is on one of the passed portals
 * @example if (isInPortal({ subs: true })) { alert("hello sub!"); }
 * @example if (isInPortal({ builder: true, owner: true })) { alert("hello builder or owner!"); }
 */
export function isInPortal(portals: IIsInPortalProps): boolean {
    const currentPortalType = getCurrentPortalType();
    if (portals.owner && currentPortalType === PortalType.OWNER) {
        return true;
    }

    if (portals.subs && currentPortalType === PortalType.SUBS) {
        return true;
    }

    if (portals.builder && currentPortalType === PortalType.BUILDER) {
        return true;
    }

    if (portals.btadmin && currentPortalType === PortalType.BTADMIN) {
        return true;
    }

    return false;
}

/**
 * @deprecated Use `UserInfoContext` in components. Kept for the non-React callers that can't
 * run a hook: `setupApix`, analytics, and `addPathForCurrentPortal`. The PortalType request
 * header uses {@link getPortalTypeForHeader} instead — see that function for why.
 */
export function getCurrentPortalType(): PortalType {
    // The condition that a path including /app/link should use the fallback for non-SPA pages will need to be removed in the future
    // This check is currently in place to address an issue where the job picker is displaying for owners when they navigate to the app via an entity link in a new tab
    // More details outlined in User Story: 173751
    const assumedPortalType = getAssumedPortalTypeForUrl(window.location.pathname ?? "");
    if (assumedPortalType !== null) {
        return assumedPortalType;
    }

    // Only reached off the SPA path: /app/link, webforms pages, and non-/app routes.
    //
    // The snapshot is null until the global info query resolves, and this runs synchronously
    // before that. `getPortalForLoginType` maps a nullish login type to `PortalType.ALL`, so
    // this stays a graceful "unknown" rather than a throw. That's fine for the non-header
    // callers of this function (URL-prefixing, analytics) — but NOT fine for the PortalType
    // request header; see `getPortalTypeForHeader`.
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- pre-resolution fallback; tracked by 282446
    return getPortalForLoginType(getSnapshotLoginType() ?? null);
}

/**
 * Resolves the `PortalType` request header, or `null` to omit it entirely.
 *
 * Mirrors the contract of the deleted JScriptGlobalsHandler.ashx, which asserted a portal only
 * when the URL proved one and otherwise deferred to the server session: send the portal the URL
 * proves ({@link getProvenPortalTypeForUrl}), else the resolved snapshot's portal, else omit the
 * header so the server's session-derived login type decides.
 *
 * This must NOT use {@link getAssumedPortalTypeForUrl}'s BUILDER default or
 * {@link getCurrentPortalType}'s unresolved-fallback value (`PortalType.ALL`) as a header value.
 * Builder URLs carry no portal prefix, so the BUILDER assumption is wrong for any sub/owner on an
 * unprefixed URL — mid-auth most of all (ado-288217) — and `PortalContextMiddleware` treats any
 * parseable header — `ALL` included — as authoritative, writing it to
 * `RequestItems.PortalLoginType`, which permanently short-circuits
 * `SessionObject.PortalLoginType`'s fallback to the real session-derived login type
 * (`_request.PortalLoginType ?? LoggedInUserLoginType`). A wrong-but-valid header is worse than no
 * header: it doesn't just mis-route this one request, it gets echoed back in that request's own
 * `GlobalInfo` response and permanently poisons `getSnapshotLoginType()` for the rest of the
 * session (ado-282449). Omitting the header instead leaves `RequestItems.PortalLoginType` unset,
 * so the server correctly falls through to the session's real login type.
 *
 * `ALL` counts as unresolved for the same reason, and reaches the snapshot by an ordinary route:
 * it is what the server reports for a request with no session (`SessionObject.PortalLoginType`
 * falling through to `LoggedInUserLoginType`), so the login page's own `GlobalInfo` response
 * latches it. It still parses as a defined `BTLoginTypes`, so sending it is as authoritative — and
 * as wrong — as any other guess. `FileUtility.AddPortalTypeQueryString` already applies this rule
 * to the query-string transport; the header matches it (ado-288217).
 *
 * Note this suppresses only a portal the URL cannot prove. Once global info has resolved, the
 * snapshot holds the server's own reported login type, and the fallback below still sends it — a
 * sub on `/app/frozen-external` sends `PortalType: 3`. That is correct: it is the value the server
 * would have derived itself, not a guess from the path.
 */
export function getPortalTypeForHeader(): PortalType | null {
    const provenPortalType = getProvenPortalTypeForUrl(window.location.pathname ?? "");
    if (provenPortalType !== null) {
        return provenPortalType;
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated -- pre-resolution fallback; tracked by 282446
    const snapshotLoginType = getSnapshotLoginType();
    return snapshotLoginType === null || snapshotLoginType === BTLoginTypes.ALL
        ? null
        : getPortalForLoginType(snapshotLoginType);
}

/** A SPA URL, excluding the portal-less `/app/link` entity-link resolver (User Story 173751). */
function isSpaUrlExcludingLink(lowerUrl: string): boolean {
    return lowerUrl.includes("/app/") && !lowerUrl.includes("/app/link");
}

/**
 * The portal the URL alone *proves* — a subs/owner/admin/share prefix — or `null` when it proves
 * nothing. Builder URLs carry no portal prefix, so this never returns BUILDER for a bare `/app/`
 * route (only for a share-token URL that names the builder portal explicitly). Callers that want
 * the builder assumption use {@link getAssumedPortalTypeForUrl}; the PortalType request header must
 * not — see {@link getPortalTypeForHeader}.
 */
export function getProvenPortalTypeForUrl(url: string): PortalType | null {
    const lowerUrl = url.toLowerCase();

    if (isSpaUrlExcludingLink(lowerUrl)) {
        if (lowerUrl.includes("/app/subs/")) {
            return PortalType.SUBS;
        }
        if (lowerUrl.includes("/app/owner/") || lowerUrl.includes("/owner/ownerinvoice")) {
            return PortalType.OWNER;
        }
        if (lowerUrl.includes("/app/admin/")) {
            return PortalType.BTADMIN;
        }

        const shareToken = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
        const builderShare = new RegExp(`/app/share/${shareToken}/builder/`, "i");
        if (builderShare.test(lowerUrl)) {
            return PortalType.BUILDER;
        }
        const subsShare = new RegExp(`/app/share/${shareToken}/subs/`, "i");
        if (subsShare.test(lowerUrl)) {
            return PortalType.SUBS;
        }
        const ownerShare = new RegExp(`/app/share/${shareToken}/owner/`, "i");
        if (ownerShare.test(lowerUrl)) {
            return PortalType.OWNER;
        }
        const contactShare = new RegExp(`/app/share/${shareToken}/contact/`, "i");
        if (contactShare.test(lowerUrl)) {
            return PortalType.OWNER;
        }
        if (lowerUrl.includes("/app/share/")) {
            return PortalType.LEAD;
        }
    }
    return null;
}

/**
 * {@link getProvenPortalTypeForUrl} plus the builder assumption: builder-portal URLs carry no
 * prefix, so an unclassified SPA route is assumed BUILDER. Right for synchronous UI decisions —
 * URL prefixing, `isInPortal`, analytics — where BUILDER is the overwhelmingly common case and
 * `null`/`ALL` would break URL building. Never for the PortalType request header, where a wrong
 * assumption poisons the server session — see {@link getPortalTypeForHeader}.
 */
export function getAssumedPortalTypeForUrl(url: string): PortalType | null {
    const provenPortalType = getProvenPortalTypeForUrl(url);
    if (provenPortalType !== null) {
        return provenPortalType;
    }

    const lowerUrl = url.toLowerCase();
    return isSpaUrlExcludingLink(lowerUrl) ? PortalType.BUILDER : null;
}

/**
 * True when a resolved user is on a URL that belongs to a different portal than their own, e.g. a
 * sub who deletes `subs` from `/app/subs/...` and lands on the builder shell.
 *
 * The server does not enforce this. The PortalType header is the session's own type (see
 * {@link getPortalTypeForHeader}), so a user's requests succeed from any URL and the URL alone
 * decides which portal renders. Callers redirect when this is true. It is the same comparison the
 * post-login redirect makes in `getLastPageRequested`.
 *
 * `loginType` is the portal the session is in (`UserInfo.loginType`), so a builder viewing
 * dedicated subs on `/app/subs/` or impersonating an owner compares as that portal. An unresolved
 * `loginType`, a portal-agnostic URL, or a non-SPA URL never matches, so this cannot fire before
 * global info loads.
 */
export function isOutsideUserPortal(
    url: string,
    loginType: BTLoginTypes | null | undefined,
): boolean {
    if (loginType == null || loginType === BTLoginTypes.ALL || isPortalAgnosticUrl(url)) {
        return false;
    }

    const urlPortal = getAssumedPortalTypeForUrl(url);
    return urlPortal !== null && urlPortal !== getPortalForLoginType(loginType);
}

/** The RUM action name for a trip of the cross-portal guard. */
export const PORTAL_GUARD_REDIRECT_ACTION = "portal_guard_redirect";

/** RUM attribute keys, matching the `bt.login_type` / `bt.portal_login_type` APM span tags. */
export const LOGIN_TYPE_RUM_KEY = "bt.login_type";
export const PORTAL_LOGIN_TYPE_RUM_KEY = "bt.portal_login_type";

/**
 * Who is logged in versus which portal they are viewing, as enum names (`SUBS`, not `3`) to match
 * the backend's tags. `UserInfo` names these confusingly: from `AccountInfo/GlobalInfo`,
 * `credentialLoginType` is the logged-in login type and `loginType` is the portal type. A value
 * that is absent or `ALL` is left out rather than reported as a real type.
 */
export function getLoginAndPortalTypeAttributes(
    credentialLoginType: BTLoginTypes | null | undefined,
    portalLoginType: BTLoginTypes | null | undefined,
): Record<string, string> {
    const attributes: Record<string, string> = {};
    if (credentialLoginType != null && credentialLoginType !== BTLoginTypes.ALL) {
        attributes[LOGIN_TYPE_RUM_KEY] = BTLoginTypes[credentialLoginType];
    }
    if (portalLoginType != null && portalLoginType !== BTLoginTypes.ALL) {
        attributes[PORTAL_LOGIN_TYPE_RUM_KEY] = BTLoginTypes[portalLoginType];
    }
    return attributes;
}

/**
 * Records a trip of the cross-portal guard as a RUM action, so each real-world redirect is
 * queryable with the user attached rather than happening silently. Fires before the redirect so
 * the action is flushed with the view it was on.
 *
 * Safe when RUM never initialized (a mocked dev session): the SDK no-ops in that case, and the
 * caller's redirect does not depend on this.
 */
export function trackPortalGuardRedirect(
    fromPath: string,
    toPath: string,
    credentialLoginType: BTLoginTypes | null | undefined,
    portalLoginType: BTLoginTypes | null | undefined,
): void {
    datadogRum.addAction(PORTAL_GUARD_REDIRECT_ACTION, {
        fromPath,
        toPath,
        ...getLoginAndPortalTypeAttributes(credentialLoginType, portalLoginType),
    });
}

/**
 * Checks the following URLs to determine if the portal should be added to the route
 * @param url The URL to check
 * @returns
 */
export function isPortalAgnosticUrl(url: string) {
    return (
        url.toLowerCase().includes("/app/link/") ||
        // Both EmailLinkClicked dispatchers: the legacy WebForms page and the APIX route it is
        // migrating to. Links in customer inboxes point at either, so both must stay matched.
        url.toLowerCase().includes("emaillinkclicked.aspx") ||
        url.toLowerCase().includes("/apix/v1/emaillinkclicked") ||
        url.toLowerCase().includes("/app/share")
    );
}

export function getPortalForLoginType(loginType: BTLoginTypes | null): PortalType {
    switch (loginType) {
        case BTLoginTypes.BUILDER:
            return PortalType.BUILDER;
        case BTLoginTypes.SUBS:
            return PortalType.SUBS;
        case BTLoginTypes.OWNER:
        case BTLoginTypes.CONTACT:
            return PortalType.OWNER;
        case BTLoginTypes.LEAD:
            return PortalType.LEAD;
        case BTLoginTypes.BTADMIN:
            return PortalType.BTADMIN;
        default:
            // should not occur in practice
            return PortalType.ALL;
    }
}

export function getLoginTypeFromPortal(portal: PortalType): BTLoginTypes {
    switch (portal) {
        case PortalType.BUILDER:
            return BTLoginTypes.BUILDER;
        case PortalType.SUBS:
            return BTLoginTypes.SUBS;
        case PortalType.OWNER:
            return BTLoginTypes.OWNER;
        case PortalType.LEAD:
            return BTLoginTypes.LEAD;
        case PortalType.BTADMIN:
            return BTLoginTypes.BTADMIN;
        default:
            // should not occur in practice
            return BTLoginTypes.ALL;
    }
}

/**
 * @summary Returns the loginType based on the current portal
 * @deprecated use UserInfoContext instead
 */
export function getLoginTypeFromCurrentPortal(): BTLoginTypes {
    return getLoginTypeFromPortal(getCurrentPortalType());
}

/**
 * Appends portalType as a query string parameter to a URL.
 * Use this for URLs that are loaded directly by the browser (e.g. img src, iframe, window.open)
 * where the PortalType header cannot be sent via apixHandler.
 * The server-side PortalContextMiddleware parses this value as BTLoginTypes.
 * @param url The URL to append the portalType to
 * @param loginType The user's login type from UserInfoContext
 * @returns The URL with the portalType query string parameter appended
 */
export function appendPortalTypeToUrl(url: string, loginType: BTLoginTypes): string {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}portalType=${loginType}`;
}

export function handleRedirectToLogin() {
    const lpr = window.location.pathname + window.location.search;

    const loginRedirect = stringifyUrl({
        url: isBTAdminPath() ? "/apix/v2/admin/auth/login" : "/",
        query: {
            lpr,
            reason: CommonConstants.Login.sessionExpired,
        },
    });

    const windowToUse = window.top ?? window;
    windowToUse.location.assign(loginRedirect);
}
